import React, { FC, useState } from 'react';
import { FeatureViewFCO, FeatureViewServingStatusSummaryWithId } from '../../../core/types/fcoTypes';
import { ServingStatusEvent, ServingStatusPanelTableDatum } from '../../servingStatus/types';
import { scaleTime } from 'd3-scale';
import { getEarliestStartDate, restructureFeatureViews } from './ServingStatus/utils';
import ServingStatusAxisRow from './ServingStatus/AxisLabel';
import ServingStatusHeaderRow from './ServingStatus/HeaderRow';
import ServingStatusTitleAndSelector from './ServingStatus/TitleAndSelector';
import ServingStatusTableRow from './ServingStatus/TableRow';
import { TimePeriodSelectorOptions } from '@tecton/ComponentRedesign';

import { Empty } from '@lithos';
import styled from '@emotion/styled';

interface TectonServingStatusSvgProps {
  width: number;
  children: React.ReactNode;
}

const ServingStateTableContainer = styled.div`
  height: 90%;
  position: relative;
  overflow: auto;
`;

const StickyRow = styled.div`
  position: sticky;
`;

export const TectonServingStatusSvg: FC<TectonServingStatusSvgProps> = ({ children, width }) => {
  return (
    <svg width={width} height={24}>
      {children}
    </svg>
  );
};

interface ServingStateChartProps {
  featureViews: FeatureViewFCO[];
  attemptSummary: FeatureViewServingStatusSummaryWithId[];
  isLoading?: boolean;
  isError?: boolean;
  hideTitle?: boolean;
  workspace: string;
}

const ServingStateChart: FC<ServingStateChartProps> = ({
  featureViews,
  attemptSummary,
  isLoading,
  isError,
  workspace,
}) => {
  const structuredDataForTable = restructureFeatureViews(featureViews, attemptSummary, '');

  const [timeSelectorOption, setTimeSelectorOption] = useState<TimePeriodSelectorOptions>({
    label: 'All Time',
    value: undefined,
  });

  const [maxWidth, setMaxWidth] = useState<number>(500);

  const [startDate, setStartDate] = useState(getEarliestStartDate(structuredDataForTable));

  const [highlightedBar, setHighlightedBar] = useState<
    | { id: string; rowIndex: number; event?: ServingStatusEvent; streamingDatum?: ServingStatusPanelTableDatum }
    | undefined
  >(undefined);

  const xScale = scaleTime().domain([startDate, new Date()]).range([0, maxWidth]);

  if (structuredDataForTable.length === 0 || structuredDataForTable[0].events.length === 0) {
    return (
      <Empty
        title="No Materialization Data"
        description="No materialization data exists."
        useCase="empty"
        placement="full-page"
        actions={[]}
      />
    );
  }

  if (isLoading) {
    return (
      <>
        <ServingStatusTitleAndSelector disabled timeSelectorOption={timeSelectorOption} hideTitle />
        <Empty title="Materialization Loading" description={<></>} useCase="empty" placement="full-page" actions={[]} />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <ServingStatusTitleAndSelector disabled timeSelectorOption={timeSelectorOption} hideTitle />
        <Empty
          title="Materialization Data Error"
          description="No Feature Views have materialization enabled."
          useCase="empty"
          placement="full-page"
          actions={[]}
        />
      </>
    );
  }

  if (attemptSummary.length === 0) {
    return (
      <>
        <ServingStatusTitleAndSelector disabled timeSelectorOption={timeSelectorOption} hideTitle />
        <Empty
          title="Materialization Not Available"
          description="No Feature Views have materialization enabled."
          useCase="empty"
          placement="full-page"
          actions={[]}
        />
      </>
    );
  }

  return (
    <>
      <ServingStatusTitleAndSelector
        timeSelectorOption={timeSelectorOption}
        setTimeSelectorOption={setTimeSelectorOption}
        setStartDate={setStartDate}
        structuredDataForTable={structuredDataForTable}
        data-testid="serving-status-title"
        hideTitle
      />
      <ServingStateTableContainer>
        <StickyRow>
          <ServingStatusHeaderRow setMaxWidth={setMaxWidth} data-testid="serving-status-header" />
          <ServingStatusAxisRow startDate={startDate} data-testid="serving-status-axis" />
        </StickyRow>
        <>
          {structuredDataForTable.map((datum, rowIndex) => {
            return (
              <ServingStatusTableRow
                datum={datum}
                startDate={startDate}
                xScale={xScale}
                rowIndex={rowIndex}
                maxWidth={maxWidth}
                highlightedBar={highlightedBar}
                setHighlightedBar={setHighlightedBar}
                structuredDataForTable={structuredDataForTable}
                data-testid="serving-status-row"
                workspace={workspace}
              />
            );
          })}
        </>
      </ServingStateTableContainer>
    </>
  );
};

export default ServingStateChart;
